import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, LogOut } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";

export default function AdminChangePasswordPage() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/care-portal/admin/login", { replace: true }); return; }
  }, [user, loading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated.");
      // Small refresh so /auth/me reflects the cleared gate, then send to Overview.
      window.location.replace("/care-portal/admin/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const doLogout = async () => { await logout(); nav("/care-portal/admin/login", { replace: true }); };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-[calc(100vh-180px)] flex items-center justify-center px-4 py-10" data-testid="admin-change-password-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-5 h-5 text-[#4A6FA5]" /><h1 className="font-heading text-xl text-slate-900">Password change required</h1></div>
        <p className="text-xs text-slate-500 mb-4">Signed in as <span className="font-medium text-slate-700">{user?.email}</span>. Please set a new password to continue to the admin dashboard.</p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Current password</span>
            <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" required className="mt-1 w-full px-3 py-2 rounded border border-slate-300 text-sm" />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">New password</span>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="cp-new" minLength={10} required className="mt-1 w-full px-3 py-2 rounded border border-slate-300 text-sm" />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Confirm new password</span>
            <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="cp-new-confirm" minLength={10} required className="mt-1 w-full px-3 py-2 rounded border border-slate-300 text-sm" />
          </label>
          <p className="text-[11px] text-slate-500">Minimum 10 characters, including at least one letter and one digit. Cannot match your email or your current password.</p>
          <button type="submit" disabled={submitting} data-testid="cp-submit" className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider"><KeyRound className="w-4 h-4" /> {submitting ? "Working…" : "Update password"}</button>
        </form>
        <button onClick={doLogout} data-testid="cp-logout" className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
      </div>
    </div>
  );
}
